import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, defer, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import {
    ActionItem,
    ActionItemStyle,
    ApiFragment,
    DottedKey,
    Family,
    FormValue,
    FormValueState,
    FragmentId,
    IconSize,
    Identifier,
    isThesaurusConceptReference,
    MakeProvider,
    MergeStrategy,
    MultiFormSelectOptions,
    MultiValueFormInput,
    NotificationType,
    PartialRecord,
    ShDelayedActionService,
    ShToastFactory,
    SuggestionMetadata,
    ThesaurusConceptReference,
    Toast
} from '@stylehaven/components';
import { partition } from 'lodash-es';
import {
    ThesaurusTreeSelection,
    ThesaurusTreeSelectionMode,
    ThesaurusTreeSelectionMulti,
    ThesaurusTreeSelectionSingle
} from '../../common/entities/common/thesaurus-tree-selection.entity';
import { BaseSandbox } from '../../common/sandboxes/base/base.sandbox.service';
import { RecordsService } from '../../common/services/api/records.service';
import { ThesaurusService } from '../../common/services/thesaurus/thesaurus.service';
import { SharedDialogHelper } from '../../dialog/dialog.module';

@Component({
    selector: 'med-form-thesaurus-base-complex',
    templateUrl: './form-thesaurus-base-complex.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(FormThesaurusComplexBaseComponent)]
})
export class FormThesaurusComplexBaseComponent
    extends MultiValueFormInput<ThesaurusConceptReference>
    implements OnInit, OnDestroy
{
    private readonly state$: BehaviorSubject<FormValue<ThesaurusConceptReference>[]> = new BehaviorSubject<
        FormValue<ThesaurusConceptReference>[]
    >([]);

    public isIndeterminate$!: Observable<boolean>;

    public tags$!: Observable<MultiFormSelectOptions<ThesaurusConceptReference>>;

    // Suggestion observables
    public suggestedConcepts$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public suggestionErrorMsg$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    public selectValuesActionItem$: Observable<ActionItem | null>;

    public synchronizeConceptSelection$: Subject<ThesaurusConceptReference | null> =
        new Subject<ThesaurusConceptReference | null>();

    public thesaurusSearchControl: UntypedFormControl = new UntypedFormControl();
    public sizes: typeof IconSize = IconSize;

    @Input() id!: string;
    @Input() label!: string;

    @Input() thesaurusDefDottedKey: DottedKey;
    @Input() recordIds: Identifier[];
    @Input() enableSuggestions = false;
    @Input() multiple = false;

    @Input() selectValuesLabel: string;
    @Input() placeholderMsg?: string;
    @Input() errorMsg!: string;
    @Input() indeterminateMsg = '';
    @Input() suggestionExistsErrorMsg: string;
    @Input() maxValuesMsg?: string;
    @Input() disabledMsg?: string;
    @Input() initialSuggestions: string[] = [];

    @Input() isDisabled = false;

    // Only used when not in bulkEdit
    @Input() activeRecord?: ApiFragment;

    @Output() suggestionsUpdate: EventEmitter<string[]> = new EventEmitter<string[]>();

    @ViewChild('search', { read: ElementRef }) searchElement: ElementRef<HTMLElement>;

    // Injectors
    private readonly delayedActionService: ShDelayedActionService = inject(ShDelayedActionService);
    private readonly sandbox: BaseSandbox = inject(BaseSandbox);
    private readonly toastFactory: ShToastFactory = inject(ShToastFactory);
    private readonly recordsService: RecordsService = inject(RecordsService);

    constructor(
        ref: ChangeDetectorRef,
        protected formBuilder: UntypedFormBuilder,
        private readonly thesaurusService: ThesaurusService,
        private readonly dialogHelper: SharedDialogHelper
    ) {
        super(ref);
    }

    ngOnInit(): void {
        this.initIndeterminateStream();

        if (this.isDisabled) {
            this.thesaurusSearchControl.disable();
        }

        if (this.multiple) {
            this.initMultiThesaurusPropagation();
            this.initTagStream();
            this.initSelectValuesActionItem();
        } else {
            this.initSingleThesaurusPropagation();
            this.initSuggestionsListener();
            this.initSelectValuesActionItem();
        }

        // Make sure that loading the suggestions doesn't overwrite the original value for single select
        if (this.initialSuggestions?.length) {
            this.suggestedConcepts$.next(this.initialSuggestions);
        }
    }

    ngOnDestroy(): void {
        this.state$.complete();
    }

    public writeValueImpl(input: FormValue<ThesaurusConceptReference>[]): void {
        /*  superclass ensures that it's an FormValue<any>[],
        here we need to check that value is ThesaurusConceptReference */
        if (!input.every(formValue => formValue.value && isThesaurusConceptReference(formValue.value))) {
            console.error(
                'ShFormThesaurusComplexBaseComponent:: value must be Array<FormValue<ThesaurusConceptReference>>',
                input
            );
            return this.writeValueImpl([]);
        }

        this.state$.next(input);

        const inputValues: number = input.filter(formValue => formValue.state === FormValueState.SET).length;

        if (!this.multiple && inputValues <= 1) {
            this.syncThesaurusSearchControl(input);
        }
    }

    private initIndeterminateStream(): void {
        this.isIndeterminate$ = this.state$.pipe(
            map(formValues => !!formValues.find(formValue => formValue.state === FormValueState.INDETERMINATE))
        );
    }

    private initTagStream(): void {
        this.tags$ = this.state$.pipe(
            map(formValues =>
                formValues.filter(formValue => formValue.state === FormValueState.SET).map(formValue => formValue.value)
            ),
            map(filteredValues => this.thesaurusReferencesToFormSelectOptions(filteredValues))
        );
    }

    private thesaurusReferencesToFormSelectOptions(
        concepts: ThesaurusConceptReference[]
    ): MultiFormSelectOptions<ThesaurusConceptReference> {
        return {
            options: concepts.map(concept => ({
                label: this.thesaurusService.getThesaurusConceptReferenceLabel(concept),
                value: concept
            })),
            isLiveData: true
        };
    }

    // #region TreeView Dialog Action Item
    private initSelectValuesActionItem(): void {
        this.selectValuesActionItem$ = of(this.getSelectValuesActionItem());
    }

    private getSelectValuesActionItem(): ActionItem | null {
        return {
            style: ActionItemStyle.Link,
            label: this.selectValuesLabel,
            eventListener: this.openThesaurusTreeDialog.bind(this),
            disabled: this.isDisabled,
            disabledInfoMsg: this.disabledMsg ?? this.maxValuesMsg
        };
    }

    private openThesaurusTreeDialog(): void {
        const thesaurusTreeSelection$: Observable<ThesaurusTreeSelection> = combineLatest([
            this.tags$ ?? of({ options: [], isLiveData: false }),
            this.suggestedConcepts$
        ]).pipe(
            take(1),
            switchMap(([tags, suggestions]) =>
                this.dialogHelper.openThesaurusTreeDialog(
                    this.thesaurusDefDottedKey,
                    ThesaurusTreeSelectionMode.ConceptSelection,
                    this.multiple,
                    this.enableSuggestions,
                    this.disabled,
                    tags,
                    suggestions,
                    true // allowEmptyResult
                )
            ),
            takeUntil(this.destroy$)
        );

        combineLatest([thesaurusTreeSelection$, this.state$, this.suggestedConcepts$])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(([thesaurusTreeSelection, currentState, currentSuggestions]) => {
                this.updateConcepts(thesaurusTreeSelection, currentState);
                this.updateSuggestions(thesaurusTreeSelection.suggestions, currentSuggestions);

                setTimeout(() => {
                    this.searchElement?.nativeElement.querySelector('button')?.focus(); // Refocus on dropdown after closing dialog
                }, 0);
            });
    }

    private updateConcepts(
        treeResult: ThesaurusTreeSelection,
        currentState: FormValue<ThesaurusConceptReference>[]
    ): void {
        if (!this.multiple) {
            const concept: ThesaurusConceptReference | null = (treeResult as ThesaurusTreeSelectionSingle).concept;
            this.updateSingleThesaurusField(concept);
            return;
        }

        const concepts: ThesaurusConceptReference[] = (treeResult as ThesaurusTreeSelectionMulti).concepts;
        if (!concepts) {
            return;
        }
        this.updateMultiThesaurusField(concepts, currentState);
    }

    // #endregion

    // #region handle Single ThesaurusField
    private initSingleThesaurusPropagation(): void {
        this.thesaurusSearchControl.valueChanges
            .pipe(
                map((values: FormValue<ThesaurusConceptReference>[] | null) => {
                    const formValues: FormValue<ThesaurusConceptReference>[] = values ?? [];

                    const setValues: FormValue<ThesaurusConceptReference>[] = formValues.filter(
                        formValue => formValue.state === FormValueState.SET
                    );

                    // propagate new values on set
                    if (setValues.length > 0) {
                        this.state$.next(setValues);
                        return setValues;
                    }

                    const indeterminateCurrentValues: FormValue<ThesaurusConceptReference>[] =
                        this.currentFormValues.filter(formValue => formValue.state === FormValueState.INDETERMINATE);

                    // unset all current value if not indeterminate
                    // MED-2264: Unsetting of single value should return a value to clear the data
                    // In this case a ThesaurusConceptReference with empty Uri
                    if (setValues.length === 0 && indeterminateCurrentValues.length === 0) {
                        const emptyValue: FormValue<ThesaurusConceptReference>[] = this.currentFormValues.map(
                            () =>
                                new FormValue(
                                    {
                                        Uri: ''
                                    },
                                    FormValueState.SET // always set!
                                )
                        );
                        return emptyValue;
                    } else {
                        // return original values if indeterminate
                        return this.currentFormValues;
                    }
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(result => {
                this.propagateChange(result);
            });
    }

    private syncThesaurusSearchControl(input: FormValue<ThesaurusConceptReference>[]): void {
        this.thesaurusSearchControl.setValue(input);
    }

    private initSuggestionsListener(): void {
        this.suggestedConcepts$
            .pipe(map(suggestions => !this.multiple && suggestions.length >= 1))
            .subscribe(disable => {
                if (disable) {
                    this.updateSingleThesaurusField(null);
                    this.thesaurusSearchControl.disable();
                    return;
                }
                this.thesaurusSearchControl.enable();
            });
    }

    private updateSingleThesaurusField(concept: ThesaurusConceptReference | null): void {
        this.synchronizeConceptSelection$.next(concept);
    }

    // #endregion

    // #region handle Multi ThesaurusField
    private initMultiThesaurusPropagation(): void {
        this.state$.pipe(takeUntil(this.destroy$)).subscribe(formValues => {
            this.propagateChange(formValues);
        });
    }

    private updateMultiThesaurusField(
        concepts: ThesaurusConceptReference[],
        currentState: FormValue<ThesaurusConceptReference>[],
        overwrite: boolean = true
    ): void {
        // Add new FormValues

        if (overwrite) {
            const formValues: FormValue<ThesaurusConceptReference>[] = concepts.map(concept => new FormValue(concept));
            const removedValues: FormValue<ThesaurusConceptReference>[] = this.currentFormValues
                .filter(currentValue => !formValues.find(newValue => newValue.value.Uri === currentValue.value.Uri))
                .map(removedValue => new FormValue(removedValue.value, FormValueState.UNSET));

            this.writeValue([...formValues, ...removedValues]);
            return;
        }

        const newValues: FormValue<ThesaurusConceptReference>[] = concepts
            .filter(concept => !currentState.find(formValue => formValue.value?.Uri === concept.Uri))
            .map(concept => new FormValue(concept));

        const updatedValues: FormValue<ThesaurusConceptReference>[] = currentState.map(formValue =>
            concepts.find(ref => ref.Uri === formValue.value.Uri) ? new FormValue(formValue.value) : formValue
        );

        const allValues: FormValue<ThesaurusConceptReference>[] = [...updatedValues, ...newValues];

        this.writeValue(allValues);
    }

    public removeValue(valueToRemove: ThesaurusConceptReference): void {
        const currentValues: FormValue<ThesaurusConceptReference>[] = this.currentFormValues;

        const foundFormValue: FormValue<ThesaurusConceptReference> | undefined = currentValues.find(
            formValue => formValue.value.Uri === valueToRemove.Uri
        );

        if (!foundFormValue) {
            return;
        }

        const conceptIndex: number = currentValues.indexOf(foundFormValue);

        const newFormValues: FormValue<ThesaurusConceptReference>[] = [...currentValues];

        if (typeof foundFormValue !== 'undefined') {
            newFormValues.splice(conceptIndex, 1, new FormValue(foundFormValue.value, FormValueState.UNSET));
        } else {
            newFormValues.splice(conceptIndex, 1);
        }

        this.state$.next(newFormValues);
    }

    // #endregion

    // #region Suggestions
    private updateSuggestions(updatedSuggestions: string[], currentSuggestions: string[]): void {
        // Split into initial suggestion that are being restored and actual new suggestions
        const [toRestore, toCheck]: [string[], string[]] = partition(updatedSuggestions, suggestion =>
            this.initialSuggestions.find(initialSuggestion => initialSuggestion === suggestion)
        );

        if (toRestore.length) {
            this.restoreInitialSuggestions(toRestore);
        }

        if (!toCheck.length) {
            return;
        }

        const newSuggestions: string[] = toCheck.filter(suggestion => !currentSuggestions.includes(suggestion));

        this.suggestionErrorMsg$.next(null);

        // Check if the new suggestions already exist
        this.thesaurusService
            .searchSuggestions(this.thesaurusDefDottedKey, newSuggestions)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(apiSuggestions => {
                const [existingSuggestions, toSubmitSuggestions]: [string[], string[]] = partition(
                    currentSuggestions.concat(newSuggestions),
                    suggestion => !!apiSuggestions.find(apiSuggestion => apiSuggestion.getTitle() === suggestion)
                );
                this.setSuggestionExistsError(existingSuggestions);

                // Add the suggestion submit stream to the queue to be excecuted later
                const suggestionSubmitStream$: Observable<string[]> = this.submitSuggestions(
                    toSubmitSuggestions.filter(toSubmit => !this.initialSuggestions.includes(toSubmit))
                ).pipe(take(1), takeUntil(this.destroy$));
                this.delayedActionService.addDelayedAction(this.thesaurusDefDottedKey, suggestionSubmitStream$);

                this.suggestedConcepts$.next(toSubmitSuggestions);
            });
    }

    private submitSuggestions(suggestions: string[]): Observable<string[]> {
        if (suggestions.length === 0) {
            return of([]);
        }

        return forkJoin(suggestions.map((suggestion: string) => this.submitSingleSuggestion(suggestion))).pipe(
            map(thesaurusSuggestions => thesaurusSuggestions.flatMap(suggestion => (suggestion ? [suggestion] : [])))
        );
    }

    private submitSingleSuggestion(suggestion: string): Observable<string | null> {
        const busyToast: Toast = this.toastFactory.create({
            message: this.sandbox.translate('action-toasts.suggestions.submit.busy'),
            type: NotificationType.Update,
            icon: 'spinner',
            iconRotate: true,
            autoClose: false
        });

        const submitStream$: Observable<string | null> = this.thesaurusService
            .submitThesaurusSuggestion(this.thesaurusDefDottedKey, this.recordIds, suggestion)
            .pipe(
                map(() => suggestion),
                tap(() => this.suggestionSubmitSuccess()),
                catchError(error => {
                    console.error('Submitting of suggestion failed', error);
                    this.suggestionSubmitFailed(suggestion, this.label);
                    return of(null);
                }),
                tap(() => this.sandbox.closeToast(busyToast.id))
            );

        return defer(() => {
            this.sandbox.addToast(busyToast);

            return submitStream$;
        });
    }

    private setSuggestionExistsError(failedSuggestions: string[]): void {
        const error: string = failedSuggestions
            .map(suggestion => this.suggestionExistsErrorMsg.replace('"term"', suggestion))
            .join('<br />');
        this.suggestionErrorMsg$.next(error);
    }

    private suggestionSubmitFailed(suggestion: string, field: string): void {
        const failedToast: Toast = this.toastFactory.create({
            message: this.sandbox.translate('action-toasts.suggestions.submit.error', { suggestion, field }),
            type: NotificationType.Error
        });

        this.sandbox.addToast(failedToast);
    }

    private suggestionSubmitSuccess(): void {
        const succcessToast: Toast = this.toastFactory.create({
            message: this.sandbox.translate('action-toasts.suggestions.submit.success'),
            type: NotificationType.Success
        });

        this.sandbox.addToast(succcessToast);
    }

    public removeSuggestion(suggestionToRemove: string): void {
        // Case if suggestion is initial suggestion
        if (this.initialSuggestions.includes(suggestionToRemove)) {
            this.removeInitialSuggestion(suggestionToRemove);
            return;
        }

        // Case if suggestion was added in this session
        this.removeCurrentSuggestion(suggestionToRemove);
    }

    private removeCurrentSuggestion(suggestionToRemove: string): void {
        this.suggestedConcepts$.next(
            this.suggestedConcepts$.value.filter(suggestion => suggestion !== suggestionToRemove)
        );

        // All new suggestions have been removed. Unregister submission action
        if (!this.suggestedConcepts$.value.filter(suggestion => !this.initialSuggestions.includes(suggestion)).length) {
            this.delayedActionService.removeDelayedAction(this.thesaurusDefDottedKey);
            return;
        }

        // Else update action with reduced set
        const suggestionSubmitStream$: Observable<string[]> = this.submitSuggestions(
            this.suggestedConcepts$.value
        ).pipe(take(1), takeUntil(this.destroy$));
        this.delayedActionService.addDelayedAction(this.thesaurusDefDottedKey, suggestionSubmitStream$);
    }

    private removeInitialSuggestion(suggestionToRemove: string): void {
        this.suggestedConcepts$.next(
            this.suggestedConcepts$.value.filter(suggestion => suggestion !== suggestionToRemove)
        );

        this.delayedActionService.addDelayedAction(
            `${this.thesaurusDefDottedKey}-remove`,
            this.getUnlinkSuggestionStream(this.suggestedConcepts$.value)
        );
    }

    private restoreInitialSuggestions(suggestionsToRestore: string[]): void {
        this.suggestedConcepts$.next(this.suggestedConcepts$.value.concat(suggestionsToRestore));

        const allInitialsRestored: boolean = this.initialSuggestions.every(initial =>
            this.suggestedConcepts$.value.find(suggestedConcept => initial === suggestedConcept)
        );

        // All initial suggestions have been restored, unregister the delete action
        if (allInitialsRestored) {
            this.delayedActionService.removeDelayedAction(`${this.thesaurusDefDottedKey}-remove`);
            return;
        }

        // Else update delete action with reduced set (overwrites old action)
        this.delayedActionService.addDelayedAction(
            `${this.thesaurusDefDottedKey}-remove`,
            this.getUnlinkSuggestionStream(this.suggestedConcepts$.value)
        );
    }

    private getUnlinkSuggestionStream(allowedSuggestionStrings: string[]): Observable<boolean> {
        // Since initial suggestions aren't show for bulk, we only need to check one record
        if (!this.activeRecord) {
            throw new Error('FormThesaurusComplexBaseComponent::activeRecord is undefined');
        }

        // Find the corresponding (non-deleted) preprocessed suggestions
        // These map labels to record ids (of the suggestions)
        const allowedSuggestions: SuggestionMetadata[] = this.activeRecord
            .get(Family.Context, 'Suggestions')
            .filter(suggestion => allowedSuggestionStrings.includes(suggestion.Descriptive.Title));

        const fragmentRelationsIds: FragmentId[] = this.activeRecord.getRelationFragmentIdsOfType('HasAsSuggestion');

        // Find the corresponding relation ids
        const newFragmentRelations: FragmentId[] = fragmentRelationsIds.filter(fragmentRelationId =>
            allowedSuggestions.find(allowedSuggestion => allowedSuggestion.Internal.FragmentId === fragmentRelationId)
        );

        const metadata: PartialRecord = new PartialRecord();
        metadata.set(Family.Structural, 'Relations', {
            ...this.activeRecord.get(Family.Structural, 'Relations'),
            HasAsSuggestion: newFragmentRelations
        });
        metadata.addMergeStrategy('Relations', MergeStrategy.OVERWRITE);

        return this.recordsService.saveRecord(this.activeRecord.getFragmentId(), [metadata]).pipe(map(() => true));
    }

    // #endregion

    public onLookupSelection(concept: ThesaurusConceptReference | null): void {
        if (!this.multiple || !concept) {
            this.updateSingleThesaurusField(concept);
            return;
        }

        this.updateMultiThesaurusField([concept], this.currentFormValues, false);
    }

    public onSuggestion(suggestion: string): void {
        this.updateSuggestions([suggestion], this.suggestedConcepts$.value);
    }

    private get currentFormValues(): FormValue<ThesaurusConceptReference>[] {
        return this.state$.value;
    }
}
